/**
 * services/sync/SyncEngine.ts
 * Core sync engine for incremental data synchronization
 *
 * Implements baseline + delta export/import with smart batching,
 * multi-tab locking, and automatic baseline compaction.
 */

import type { Table } from 'dexie';
import { db } from '@/lib/db';
import type { SyncProvider, SchemaVersionError } from '@/types/sync/SyncProvider';
import type {
  BaselineExport,
  DeltaExport,
  SyncManifest,
  SyncResult,
} from '@/types/sync/SyncExport';
import {
  generateBaselineFilename,
  generateDeltaFilename,
  SYNC_MANIFEST_FILENAME,
  isBaselineExport,
  isDeltaExport,
} from '@/types/sync/SyncExport';
import { isTombstoned } from '@/utils/tombstone';
import { getOrCreateDeviceId } from '@/utils/deviceDetection';
import { syncCoordinatorService } from './SyncCoordinatorService';
import {
  OP_LOG_WARNING_THRESHOLD,
  SYNC_EXCLUDED_RECORDS,
  SYNC_SCHEMA_VERSION,
  TOMBSTONE_RETENTION_DAYS,
} from './constants';
import { SYNC_TABLES, type SyncTableName } from '@/constants/syncTables';
import { markAsImportContext } from './SyncImportContext';
import { getSyncOperationService } from './SyncOperationService';
import type { SyncOperation } from '@/types/sync/SyncOperation';
import {
  processOperations,
  detectSequenceGaps,
  expandCompactedSeqs,
  isCanonicalIso,
  type ProcessOperationsResult,
  type GapDetectionResult,
  type SyncRecord,
} from './OperationApplicator';
import { compactOperations } from './OperationCompactor';

/**
 * Result of importing a delta with operation-based sync support.
 * Includes gap detection info and operation processing statistics.
 */
export interface DeltaImportResult {
  /** Gap detection result (undefined if no operations in delta) */
  gapInfo?: GapDetectionResult;
  /** Operation processing statistics (undefined if no operations in delta) */
  operationStats?: ProcessOperationsResult['stats'];
}

/**
 * Result of exporting a delta with compaction support.
 * Includes the delta for writing and opIds for purge tracking.
 */
export interface ExportDeltaResult {
  /** The exportable delta (after compaction) */
  delta: DeltaExport;
  /** All original opIds included in this export (for purge tracking) */
  originalOpIds: string[];
}

/**
 * Snapshot of manifest state at preflight start for consistency checking.
 * Used to detect concurrent manifest changes during import.
 */
interface ManifestSnapshot {
  /** ISO timestamp of most recent export */
  lastExportedAt: string;
  /** Current baseline filename */
  currentBaseline: string;
  /** Number of deltas in manifest (fallback comparison key) */
  deltaCount: number;
  /** Last delta filename (catches appended deltas even if count unchanged due to compaction) */
  lastDeltaFilename: string | null;
}

/**
 * Collected operations from all parsed deltas for batch processing.
 * Enables global gap detection and atomic batch validation.
 */
interface CollectedOperations {
  /** All operations from all deltas (including same-device for gap detection) */
  operations: SyncOperation[];
  /** Operations grouped by device for gap filtering */
  byDeviceOps: Map<string, SyncOperation[]>;
  /** Merged opSeqRanges from all deltas */
  opSeqRanges: Record<string, { minSeq: number; maxSeq: number }>;
  /** Merged compactedSeqs from all deltas - normalized to Set<number> per device */
  compactedSeqs: Record<string, Set<number>>;
  /** Devices that contributed operations (excluding local device) */
  remoteDevices: Set<string>;
  /** This device's ID (for skip logic at apply time) */
  localDeviceId: string;
  /** Devices with detected sequence gaps (excluded from this import cycle) */
  gappedDevices: Set<string>;
}

/**
 * Result of gap detection for a single device.
 * Only tracks min/max/count to avoid memory spikes for large gaps.
 */
interface DeviceGapResult {
  hasGaps: boolean;
  minMissing: number;
  maxMissing: number;
  count: number;
}

/**
 * Detect sequence gaps for a device by building complete seq set.
 * Unlike detectSequenceGaps() which checks ranges, this checks actual seqs.
 *
 * Memory-optimized: only tracks min/max/count, not all missing seqs.
 *
 * @param deviceOps - All operations from this device across all deltas
 * @param compactedSeqs - Normalized set of compacted sequence numbers
 * @param lastAppliedSeq - Last sequence we've applied for this device
 * @returns Gap result with min/max/count, or hasGaps=false if no gaps
 */
function detectDeviceGaps(
  deviceOps: SyncOperation[],
  compactedSeqs: Set<number>,
  lastAppliedSeq: number
): DeviceGapResult {
  // Build set of all seen sequences
  const seenSeqs = new Set<number>();
  let maxSeq = lastAppliedSeq;

  for (const op of deviceOps) {
    seenSeqs.add(op.seq);
    if (op.seq > maxSeq) maxSeq = op.seq;
  }

  // Add compacted sequences
  for (const seq of compactedSeqs) {
    seenSeqs.add(seq);
    if (seq > maxSeq) maxSeq = seq;
  }

  // Scan for gaps - only track min/max/count (memory-optimized per GPT feedback)
  let minMissing = -1;
  let maxMissing = -1;
  let count = 0;

  for (let seq = lastAppliedSeq + 1; seq <= maxSeq; seq++) {
    if (!seenSeqs.has(seq)) {
      if (minMissing === -1) minMissing = seq;
      maxMissing = seq;
      count++;
    }
  }

  return {
    hasGaps: count > 0,
    minMissing,
    maxMissing,
    count,
  };
}

/**
 * Info about a batch of operations sharing the same batchId.
 */
interface BatchInfo {
  batchId: string;
  deviceId: string;
  ops: SyncOperation[];
  minSeq: number;
  maxSeq: number;
}

/**
 * Validate batch completeness for operations with batchId.
 * Defensive guard: detects batches that span the stale boundary (some ops already
 * applied, some not). Should never trigger in normal operation.
 *
 * @param operations - All operations to validate (already filtered for gapped devices)
 * @param lastAppliedByDevice - Merged seq tracking from local + baseline
 * @param localDeviceId - Local device ID to exclude (same-device ops are never applied)
 * @returns Set of operations that belong to incomplete batches (should be removed)
 */
function validateBatches(
  operations: SyncOperation[],
  lastAppliedByDevice: Record<string, number>,
  localDeviceId: string
): Set<SyncOperation> {
  const incompleteBatchOps = new Set<SyncOperation>();

  // Group ops by (deviceId, batchId) - only ops with batchId, exclude local device
  const batches = new Map<string, BatchInfo>();
  for (const op of operations) {
    if (!op.batchId) continue; // Ops without batchId bypass this check
    if (op.deviceId === localDeviceId) continue; // Same-device ops are never applied

    const key = `${op.deviceId}:${op.batchId}`;
    let batch = batches.get(key);
    if (!batch) {
      batch = {
        batchId: op.batchId,
        deviceId: op.deviceId,
        ops: [],
        minSeq: op.seq,
        maxSeq: op.seq,
      };
      batches.set(key, batch);
    }
    batch.ops.push(op);
    batch.minSeq = Math.min(batch.minSeq, op.seq);
    batch.maxSeq = Math.max(batch.maxSeq, op.seq);
  }

  // Check each batch for stale boundary crossing
  for (const batch of batches.values()) {
    const lastApplied = lastAppliedByDevice[batch.deviceId] ?? 0;

    // Batch spans stale boundary if some ops are stale and some are new
    const hasStaleOps = batch.minSeq <= lastApplied;
    const hasNewOps = batch.maxSeq > lastApplied;

    if (hasStaleOps && hasNewOps) {
      // Mixed batch - incomplete (spans stale boundary)
      console.warn(
        `⚠️ [SyncEngine] Skipping incomplete batch ${batch.batchId.slice(0, 8)}: ` +
          `device=${batch.deviceId.slice(0, 8)}, seqs ${batch.minSeq}-${batch.maxSeq} ` +
          `spans lastApplied=${lastApplied}. ${batch.ops.length} ops excluded.`
      );
      for (const op of batch.ops) {
        incompleteBatchOps.add(op);
      }
    }
    // Fully stale (all <= lastApplied): OK - idempotency will skip all
    // Fully new (all > lastApplied): OK - all will apply
  }

  return incompleteBatchOps;
}

/**
 * Calculate the highest contiguous sequence number from lastApplied.
 *
 * This is a defensive check to ensure we only advance lastAppliedSeqByDevice
 * to sequences we've actually seen (either in operations or compacted seqs).
 * In a healthy system, this should always equal maxSeq after Phase 2 gap filtering.
 *
 * @param lastApplied - The last applied sequence for this device
 * @param seenSeqs - Set of all seen sequences (ops + compacted)
 * @param maxSeq - The maximum sequence seen for this device
 * @returns Highest contiguous sequence from lastApplied
 */
function calculateContiguousSeq(
  lastApplied: number,
  seenSeqs: Set<number>,
  maxSeq: number
): number {
  // If no new seqs beyond lastApplied, return unchanged
  if (maxSeq <= lastApplied) return lastApplied;

  // Scan from lastApplied+1 to maxSeq for first missing seq
  for (let seq = lastApplied + 1; seq <= maxSeq; seq++) {
    if (!seenSeqs.has(seq)) {
      return seq - 1; // Last contiguous is one before the gap
    }
  }

  // All contiguous through maxSeq
  return maxSeq;
}

/**
 * Type-safe table accessor helper
 *
 * @param tableName - Name of the table to access
 * @returns Dexie table instance
 */
function getTable(tableName: SyncTableName) {
  return db[tableName];
}

/**
 * Update sync_state with partial fields, preserving existing values.
 *
 * This helper reads existing sync_state, merges new fields, and writes back.
 * Prevents field loss when updating only specific fields (e.g., lastSyncedDeltaFilename).
 *
 * @param deviceId - Device ID (primary key for sync_state)
 * @param updates - Partial fields to merge into sync_state
 */
async function updateSyncStatePartial(
  deviceId: string,
  updates: Partial<Omit<import('@/types/sync/SyncState').SyncState, 'id'>>
): Promise<void> {
  const existing = await db.sync_state.get(deviceId);
  const now = new Date().toISOString();

  if (existing) {
    // Merge: existing values + updates (updates take precedence)
    await db.sync_state.put({
      ...existing,
      ...updates,
      id: deviceId, // Ensure id is never overwritten
      updatedAt: now,
    });
  } else {
    // First time: create with defaults + updates
    await db.sync_state.put({
      id: deviceId,
      createdAt: now,
      updatedAt: now,
      lastSyncedAt: new Date(0).toISOString(),
      lastSyncDirection: 'import',
      pendingChanges: 0,
      syncProvider: 'local',
      ...updates,
    });
  }
}

/**
 * Get all sync table instances for transaction scope
 *
 * Returns Dexie Table instances for all tables in SYNC_TABLES.
 * Used to declare transaction scope for atomic import operations.
 *
 * @throws Error if any table in SYNC_TABLES is not found in db (schema drift detection)
 * @returns Array of Dexie Table instances
 */
function getSyncTableInstances(): Table[] {
  return SYNC_TABLES.map((name) => {
    const table = db[name];
    if (!table) {
      throw new Error(
        `[SyncEngine] Table "${name}" not found in database. SYNC_TABLES may be out of sync with schema.`
      );
    }
    return table;
  });
}

// NOTE: MERGE_TABLES and getMergeTableNames() removed in T11 Phase 3.
// State-based section merge is no longer used - all sync is operation-based.

/**
 * Filter out excluded records from export data.
 *
 * Used by both exportBaseline() and detectChanges() to ensure consistency.
 * Filters by record ID regardless of tombstone status.
 *
 * @param tableName - Name of the table being exported
 * @param records - Array of records from the table
 * @returns Filtered array with excluded records removed
 */
function filterExcludedRecords<T extends { id: string }>(
  tableName: string,
  records: T[]
): T[] {
  const excludedIds = SYNC_EXCLUDED_RECORDS[tableName];
  if (!excludedIds || excludedIds.length === 0) {
    return records;
  }
  return records.filter((record) => !excludedIds.includes(record.id));
}

// NOTE: SYNC_LOCK_KEY and SYNC_LOCK_TIMEOUT moved to services/sync/constants.ts
// (now used by SyncCoordinatorService for cross-tab mutex)

/**
 * Maximum number of deltas before baseline compaction
 */
const MAX_DELTAS_BEFORE_COMPACTION = 30;

/**
 * Maximum days between baselines (90 days)
 */
const MAX_DAYS_BETWEEN_BASELINES = 90;

/**
 * Delta size threshold as percentage of baseline size (50%)
 * TODO: Implement size comparison when SyncProvider.list() includes size metadata
 */
// const DELTA_SIZE_THRESHOLD_PERCENT = 0.5;

// NOTE: SyncLock interface removed - now handled by SyncCoordinatorService

/**
 * Change detection result
 */
interface ChangeDetectionResult {
  /** Total number of changed records across all tables */
  totalChanges: number;
  /** Changes organized by table */
  changesByTable: Record<string, unknown[]>;
}

// ===== TOMBSTONE DELETION HELPERS =====
// See CODING_STANDARDS.md "Tombstone Retention Policy" for full documentation.
//
// WARNING: Cloud data created before Dec 9, 2025 may lack tombstones.
// Pre-tombstone baselines can cause "resurrection" of deleted records during import.

/**
 * Record metadata for tombstone intent comparison
 */
interface RecordMeta {
  /** Timestamp as milliseconds (0 if missing/invalid) */
  timestamp: number;
  /** Device ID (undefined if missing) */
  deviceId: string | undefined;
}

/**
 * Extract timestamp and deviceId from a record for intent comparison.
 *
 * Defensive: handles missing fields and non-string timestamps gracefully.
 *
 * @param record - Any record (may be tombstoned or not)
 * @returns Metadata with timestamp (ms) and deviceId
 */
function extractRecordMeta(record: unknown): RecordMeta {
  if (!record || typeof record !== 'object') {
    return { timestamp: 0, deviceId: undefined };
  }

  const obj = record as Record<string, unknown>;

  // Extract timestamp: prefer updatedAt, fall back to lastModified
  let timestamp = 0;
  const tsValue = obj['updatedAt'] ?? obj['lastModified'];
  if (typeof tsValue === 'string' && tsValue.length > 0) {
    const parsed = new Date(tsValue).getTime();
    if (!isNaN(parsed)) {
      timestamp = parsed;
    }
  }

  // Extract deviceId: prefer deletedByDevice (for tombstones), fall back to deviceId field
  let deviceId: string | undefined;
  const deviceValue = obj['deletedByDevice'] ?? obj['deviceId'];
  if (typeof deviceValue === 'string' && deviceValue.length > 0) {
    deviceId = deviceValue;
  }

  return { timestamp, deviceId };
}

/**
 * Determine if a remote tombstone should be accepted over local data.
 *
 * INTENT RULES:
 * - Rule 1: Same-device deletion accepted only if remote is newer
 * - Rule 2: Cross-device deletion accepted if remote is newer
 *
 * Both rules require remote to be newer to prevent stale tombstones
 * from overriding newer data.
 *
 * @param local - Local record (exists, may or may not be tombstoned)
 * @param remote - Remote record (is tombstoned)
 * @returns True if remote tombstone should replace local
 */
function shouldAcceptRemoteDeletion(local: unknown, remote: unknown): boolean {
  const localMeta = extractRecordMeta(local);
  const remoteMeta = extractRecordMeta(remote);

  // INTENT RULE: Deletion accepted only if remote is newer
  // This applies to both same-device and cross-device scenarios.
  // Stale tombstones should never override newer data.
  return remoteMeta.timestamp > localMeta.timestamp;
}

/**
 * Determine if a tombstoned local record should be resurrected by remote data.
 *
 * RESURRECTION PROTECTION:
 * - Only allow resurrection if remote is newer AND from the same device
 * - This prevents accidental resurrection from stale non-tombstoned data
 *   on other devices that haven't received the deletion yet
 *
 * @param localTombstone - Local tombstoned record
 * @param remote - Remote record (not tombstoned)
 * @returns True if resurrection should be allowed
 */
function shouldResurrect(localTombstone: unknown, remote: unknown): boolean {
  const localMeta = extractRecordMeta(localTombstone);
  const remoteMeta = extractRecordMeta(remote);

  // RESURRECTION RULE: Only allow if remote is newer AND same device
  // This handles the case where a user deleted on device A, then device A
  // creates new data that should replace the tombstone.
  const remoteIsNewer = remoteMeta.timestamp > localMeta.timestamp;
  const sameDevice = remoteMeta.deviceId !== undefined &&
    remoteMeta.deviceId === localMeta.deviceId;

  return remoteIsNewer && sameDevice;
}

/**
 * Determine if a tombstone record is expired based on retention period.
 *
 * Used during baseline exports to filter out old tombstones that are no longer
 * needed for cross-device sync. Local IndexedDB retains all tombstones.
 *
 * Boundary condition: ageMs >= retentionMs means expired (90 days exactly = expired).
 *
 * @param record - Any record to check (handles unknown input for tolerance)
 * @param retentionDays - Retention period in days (use TOMBSTONE_RETENTION_DAYS)
 * @returns True if record is an expired tombstone that should be filtered out
 */
function isExpiredTombstone(record: unknown, retentionDays: number): boolean {
  // Not a tombstone - keep the record
  if (!isTombstoned(record)) {
    return false;
  }

  // At this point, TypeScript knows record has deletedAt: string
  const deletedAtMs = new Date(record.deletedAt).getTime();

  // Invalid date - fail safe by keeping the record
  if (Number.isNaN(deletedAtMs)) {
    return false;
  }

  const ageMs = Date.now() - deletedAtMs;
  const retentionMs = retentionDays * 24 * 60 * 60 * 1000;

  return ageMs >= retentionMs;
}

// ===== SCHEMA VERSION VALIDATION =====

/**
 * Parsed delta with its filename for validation and import.
 */
interface ParsedDelta {
  delta: DeltaExport;
  filename: string;
}

/**
 * Validate schema versions across all sync artifacts before import.
 *
 * Checks:
 * 1. Baseline has valid schemaVersion (present, number, not NaN)
 * 2. Baseline version is not newer than local (blocks stale browser)
 * 3. Delta versions are within allowed range:
 *    - In migration scenario (baselineVersion < localVersion): allow [baselineVersion, localVersion]
 *    - In standard case: require deltaVersion === baselineVersion
 * 4. Logs warning if importing older version or mixed delta versions (migration diagnostics)
 *
 * @param baseline - Parsed baseline export
 * @param baselineName - Filename of baseline (for error reporting)
 * @param deltas - Array of parsed deltas with filenames
 * @param localVersion - Current app's SYNC_SCHEMA_VERSION
 * @returns SchemaVersionError if validation fails, null if valid
 */
function validateSchemaVersions(
  baseline: BaselineExport,
  baselineName: string,
  deltas: ParsedDelta[],
  localVersion: number
): SchemaVersionError | null {
  // Step 1: Validate baseline schemaVersion is present and valid
  const baselineVersion = baseline.schemaVersion;

  if (baselineVersion === undefined || baselineVersion === null) {
    const error: SchemaVersionError = {
      code: 'SCHEMA_VERSION_MISSING',
      message: 'Baseline is missing schemaVersion field',
      localVersion,
      remoteVersion: null,
      artifactType: 'baseline',
      artifactName: baselineName,
    };
    console.error('[SyncEngine] Schema version validation failed', {
      code: error.code,
      localVersion,
      remoteVersion: null,
      artifactType: 'baseline',
      artifactName: baselineName,
    });
    return error;
  }

  if (typeof baselineVersion !== 'number' || isNaN(baselineVersion)) {
    const error: SchemaVersionError = {
      code: 'SCHEMA_VERSION_INVALID',
      message: `Baseline has invalid schemaVersion: ${String(baselineVersion)}`,
      localVersion,
      remoteVersion: null,
      artifactType: 'baseline',
      artifactName: baselineName,
    };
    console.error('[SyncEngine] Schema version validation failed', {
      code: error.code,
      localVersion,
      remoteVersion: String(baselineVersion),
      artifactType: 'baseline',
      artifactName: baselineName,
    });
    return error;
  }

  // Step 2: Check if baseline version is newer than local (stale browser)
  if (baselineVersion > localVersion) {
    const error: SchemaVersionError = {
      code: 'SCHEMA_VERSION_NEWER',
      message: 'Sync paused - please refresh your browser to continue syncing',
      localVersion,
      remoteVersion: baselineVersion,
      artifactType: 'baseline',
      artifactName: baselineName,
    };
    console.error('[SyncEngine] Schema version validation failed', {
      code: error.code,
      localVersion,
      remoteVersion: baselineVersion,
      artifactType: 'baseline',
      artifactName: baselineName,
    });
    return error;
  }

  // Step 3: Validate all deltas have valid schemaVersion
  // NOTE: We validate ALL deltas including same-device ones - corruption is corruption
  //
  // Migration scenario handling:
  // When baselineVersion < localVersion, the app has been upgraded but the baseline
  // hasn't been compacted yet. During this window, new deltas are created at localVersion
  // while the baseline remains at the old version. We allow deltas in the range
  // [baselineVersion, localVersion] to support this migration gap.
  const isMigrationScenario = baselineVersion < localVersion;
  const deltaVersionsSeen = new Set<number>();

  for (const { delta, filename } of deltas) {
    const deltaVersion = delta.schemaVersion;

    // Check delta has valid schemaVersion
    if (deltaVersion === undefined || deltaVersion === null) {
      const error: SchemaVersionError = {
        code: 'SCHEMA_VERSION_MISSING',
        message: `Delta is missing schemaVersion field`,
        localVersion,
        remoteVersion: null,
        artifactType: 'delta',
        artifactName: filename,
        baselineVersion,
      };
      console.error('[SyncEngine] Schema version validation failed', {
        code: error.code,
        localVersion,
        remoteVersion: null,
        artifactType: 'delta',
        artifactName: filename,
        baselineVersion,
      });
      return error;
    }

    if (typeof deltaVersion !== 'number' || isNaN(deltaVersion)) {
      const error: SchemaVersionError = {
        code: 'SCHEMA_VERSION_INVALID',
        message: `Delta has invalid schemaVersion: ${String(deltaVersion)}`,
        localVersion,
        remoteVersion: null,
        artifactType: 'delta',
        artifactName: filename,
        baselineVersion,
      };
      console.error('[SyncEngine] Schema version validation failed', {
        code: error.code,
        localVersion,
        remoteVersion: String(deltaVersion),
        artifactType: 'delta',
        artifactName: filename,
        baselineVersion,
      });
      return error;
    }

    // Track versions seen for migration warning
    deltaVersionsSeen.add(deltaVersion);

    // Validate delta version against allowed range
    // Error if delta is from the future (newer than local app version)
    // Use SCHEMA_VERSION_NEWER so UI shows "refresh browser" instead of "data corrupted"
    if (deltaVersion > localVersion) {
      const error: SchemaVersionError = {
        code: 'SCHEMA_VERSION_NEWER',
        message: 'Sync paused - please refresh your browser to continue syncing',
        localVersion,
        remoteVersion: deltaVersion,
        artifactType: 'delta',
        artifactName: filename,
        baselineVersion,
      };
      console.error('[SyncEngine] Schema version validation failed', {
        code: error.code,
        localVersion,
        remoteVersion: deltaVersion,
        artifactType: 'delta',
        artifactName: filename,
        baselineVersion,
      });
      return error;
    }

    // Error if delta is older than baseline (corrupted/orphaned delta)
    if (deltaVersion < baselineVersion) {
      const error: SchemaVersionError = {
        code: 'SCHEMA_VERSION_MISMATCH',
        message: `Delta version (v${deltaVersion}) is older than baseline (v${baselineVersion})`,
        localVersion,
        remoteVersion: deltaVersion,
        artifactType: 'delta',
        artifactName: filename,
        baselineVersion,
      };
      console.error('[SyncEngine] Schema version validation failed', {
        code: error.code,
        localVersion,
        remoteVersion: deltaVersion,
        artifactType: 'delta',
        artifactName: filename,
        baselineVersion,
      });
      return error;
    }

    // In migration scenario, allow deltas in range [baselineVersion, localVersion]
    // In standard case (no migration), require exact match with baseline
    if (!isMigrationScenario && deltaVersion !== baselineVersion) {
      const error: SchemaVersionError = {
        code: 'SCHEMA_VERSION_MISMATCH',
        message: `Delta version (v${deltaVersion}) doesn't match baseline (v${baselineVersion})`,
        localVersion,
        remoteVersion: deltaVersion,
        artifactType: 'delta',
        artifactName: filename,
        baselineVersion,
      };
      console.error('[SyncEngine] Schema version validation failed', {
        code: error.code,
        localVersion,
        remoteVersion: deltaVersion,
        artifactType: 'delta',
        artifactName: filename,
        baselineVersion,
      });
      return error;
    }
  }

  // Step 4: Log warnings for migration scenarios
  if (isMigrationScenario) {
    // Log migration scenario with version details
    console.warn('[SyncEngine] Importing older schema version (migration)', {
      localVersion,
      baselineVersion,
      deltaVersions: Array.from(deltaVersionsSeen).sort((a, b) => a - b),
    });

    // Additional warning if mixed delta versions detected during migration
    if (deltaVersionsSeen.size > 1) {
      console.warn('[SyncEngine] Mixed delta versions detected during migration', {
        localVersion,
        baselineVersion,
        deltaVersions: Array.from(deltaVersionsSeen).sort((a, b) => a - b),
        recommendation: 'Consider triggering baseline compaction after import',
      });
    }
  }

  // All checks passed
  return null;
}

/**
 * Core sync engine service
 *
 * Provides incremental sync with baseline + delta files, multi-tab
 * coordination, and automatic baseline compaction.
 */
export class SyncEngine {
  private provider: SyncProvider | null = null;
  private deviceId: string;
  private userId: string;

  constructor(userId: string, deviceId: string) {
    this.userId = userId;
    this.deviceId = deviceId;
  }

  // ===== PROVIDER MANAGEMENT =====

  /**
   * Set the sync provider (local directory, Google Drive, etc.)
   *
   * @param provider - Configured sync provider instance
   */
  setProvider(provider: SyncProvider): void {
    this.provider = provider;
  }

  /**
   * Get the user ID for this sync engine
   *
   * @returns User ID
   */
  getUserId(): string {
    return this.userId;
  }

  /**
   * Get the device ID for this sync engine
   *
   * @returns Device ID
   */
  getDeviceId(): string {
    return this.deviceId;
  }

  /**
   * Get the current sync provider
   *
   * @returns Current provider or null if not set
   */
  getProvider(): SyncProvider | null {
    return this.provider;
  }

  // ===== SYNC LOCK MANAGEMENT =====

  // ===== CHANGE DETECTION =====
  // NOTE: Sync lock methods (acquireSyncLock/releaseSyncLock) removed in favor of
  // SyncCoordinatorService.tryAcquireExportLock/releaseExportLock (cross-tab mutex with TTL)

  /**
   * Detect all changed records since last sync
   *
   * Queries all user data tables for records with updatedAt > sinceTimestamp
   *
   * @param sinceTimestamp - ISO timestamp to check changes since
   * @returns Change detection result with counts and data
   */
  async detectChanges(sinceTimestamp: string): Promise<ChangeDetectionResult> {
    const changesByTable: Record<string, unknown[]> = {};
    let totalChanges = 0;

    for (const tableName of SYNC_TABLES) {
      try {
        // Query for records updated since last sync
        const changedRecords = await db[tableName]
          .where('updatedAt')
          .above(sinceTimestamp)
          .toArray();

        // Filter out excluded records (e.g., beta application draft)
        const filteredRecords = filterExcludedRecords(
          tableName,
          changedRecords as { id: string }[]
        );

        if (filteredRecords.length > 0) {
          changesByTable[tableName] = filteredRecords;
          totalChanges += filteredRecords.length;
        }
      } catch (error) {
        console.error(`Error detecting changes in ${tableName}:`, error);
        throw new Error(`Failed to detect changes in ${tableName}`);
      }
    }

    return { totalChanges, changesByTable };
  }

  // ===== BASELINE EXPORT =====

  /**
   * Export complete baseline snapshot of all user data
   *
   * Creates a baseline file containing all records from all tables.
   * Used for first sync or during baseline compaction.
   *
   * @returns Baseline export data
   */
  async exportBaseline(): Promise<BaselineExport> {
    const now = new Date().toISOString();

    // Fetch sync state to embed lastAppliedSeqByDevice in baseline
    const syncState = await db.sync_state.get(this.deviceId);
    const lastAppliedSeqByDevice = syncState?.lastAppliedSeqByDevice ?? {};

    const data: BaselineExport['data'] = {};

    for (const tableName of SYNC_TABLES) {
      try {
        const records = await db[tableName].toArray();

        // Filter out excluded records (e.g., beta application draft)
        const recordsAfterExclusions = filterExcludedRecords(
          tableName,
          records as { id: string }[]
        );

        // Filter out expired tombstones (>90 days old)
        const recordsAfterTombstones = recordsAfterExclusions.filter(
          (record) => !isExpiredTombstone(record, TOMBSTONE_RETENTION_DAYS)
        );

        if (recordsAfterTombstones.length > 0) {
          data[tableName] = recordsAfterTombstones;
        }
      } catch (error) {
        console.error(`Error exporting baseline from ${tableName}:`, error);
        throw new Error(`Failed to export baseline from ${tableName}`);
      }
    }

    const baseline: BaselineExport = {
      type: 'baseline',
      exportedAt: now,
      userId: this.userId,
      deviceId: this.deviceId,
      schemaVersion: SYNC_SCHEMA_VERSION,
      lastAppliedSeqByDevice,
      data,
    };

    return baseline;
  }

  /**
   * Write baseline to sync provider with verification and retry
   *
   * Wraps write + verify in retryWithBackoff() for resilience against
   * transient failures. Each retry attempts the full write+verify cycle.
   *
   * @param baseline - Baseline export data
   * @returns Filename of written baseline
   * @throws Error if all retries exhausted
   */
  private async writeBaseline(baseline: BaselineExport): Promise<string> {
    if (!this.provider) {
      throw new Error('Sync provider not set');
    }

    const filename = generateBaselineFilename(baseline.exportedAt);
    const json = JSON.stringify(baseline, null, 2);
    const expectedSize = new TextEncoder().encode(json).length;

    await this.retryWithBackoff(async () => {
      if (!this.provider) {
        throw new Error('Sync provider not set');
      }
      await this.provider.write(filename, json);
      await this.verifyFileWrite(filename, expectedSize);
    }, { label: 'baseline write+verify' });

    console.log(`✅ Baseline written and verified: ${filename} (${expectedSize} bytes)`);

    return filename;
  }

  // ===== DELTA EXPORT =====

  /**
   * Export delta of changed records since last sync
   *
   * Creates a delta file containing only records that changed since
   * the specified timestamp, plus pending sync operations.
   *
   * Note: This method is read-only with respect to operation log state.
   * Operations are NOT marked as synced here - that happens in a separate
   * post-upload step after successful verification.
   *
   * @param sinceTimestamp - ISO timestamp to detect changes since
   * @returns ExportDeltaResult with delta and originalOpIds for purge tracking
   */
  async exportDelta(sinceTimestamp: string): Promise<ExportDeltaResult> {
    const now = new Date().toISOString();

    // Fetch pending operations for this device (already ordered by seq)
    const pendingOps = await getSyncOperationService().getPendingOps();

    // Warn if op log size exceeds threshold (diagnostic only, does not block)
    if (pendingOps.length > OP_LOG_WARNING_THRESHOLD) {
      console.warn(
        `⚠️  Op log size (${pendingOps.length}) exceeds threshold (${OP_LOG_WARNING_THRESHOLD}). Consider syncing more frequently.`
      );
    }

    // Strip 'synced' field from each operation for export
    // StoredSyncOperation has 'synced: boolean' which is storage-only
    const operations: SyncOperation[] = pendingOps.map((storedOp) => {
      const { synced: _synced, ...operation } = storedOp;
      return operation;
    });

    // Compact operations: coalesce multiple ops on same (table, recordId, field)
    // This reduces delta size and simplifies import processing
    const compactionResult = compactOperations(operations);

    // Compute opSeqRanges from ORIGINAL ops (pre-compaction) for gap detection
    // This reflects the full range of sequences included in this export
    let opSeqRanges: { [deviceId: string]: { minSeq: number; maxSeq: number } } = {};
    const firstOp = pendingOps[0];
    const lastOp = pendingOps[pendingOps.length - 1];
    if (firstOp !== undefined && lastOp !== undefined) {
      opSeqRanges = {
        [firstOp.deviceId]: {
          minSeq: firstOp.seq,
          maxSeq: lastOp.seq,
        },
      };
    }

    // Build delta with compacted operations (ops-only, no state-based changes)
    const delta: DeltaExport = {
      type: 'delta',
      exportedAt: now,
      userId: this.userId,
      deviceId: this.deviceId,
      sinceTimestamp,
      schemaVersion: SYNC_SCHEMA_VERSION,
      operations: compactionResult.operations,
      opSeqRanges,
    };

    // Populate compactedSeqs only if compaction dropped sequences
    // Omit field entirely when empty (v1 compatibility semantics)
    if (Object.keys(compactionResult.droppedSeqsByDevice).length > 0) {
      delta.compactedSeqs = compactionResult.droppedSeqsByDevice;
    }

    return {
      delta,
      originalOpIds: compactionResult.originalOpIds,
    };
  }

  // ===== RETRY INFRASTRUCTURE =====

  /**
   * Execute an async function with retry logic and exponential backoff.
   *
   * Retries on any error, with configurable delays between attempts.
   * Default: 3 retries with 1s, 2s, 4s delays (4 total attempts).
   *
   * Used for delta/manifest write operations to handle transient failures.
   *
   * @param fn - Async function to execute
   * @param options - Configuration options
   * @param options.delays - Array of delay durations in ms (default: [1000, 2000, 4000])
   * @param options.label - Label for logging (default: 'operation')
   * @returns Result of fn() on success
   * @throws Last error after all retries exhausted
   */
  private async retryWithBackoff<T>(
    fn: () => Promise<T>,
    options?: { delays?: number[]; label?: string }
  ): Promise<T> {
    const delays = options?.delays ?? [1000, 2000, 4000];
    const label = options?.label ?? 'operation';
    const maxAttempts = delays.length + 1; // Initial attempt + retries

    let lastError: Error | undefined;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        const retriesRemaining = maxAttempts - attempt;

        if (retriesRemaining > 0) {
          const delayMs = delays[attempt - 1] ?? delays[delays.length - 1] ?? 1000;
          console.warn(
            `⚠️  [${label}] Attempt ${attempt}/${maxAttempts} failed, ${retriesRemaining} retries remaining. ` +
            `Retrying in ${delayMs}ms... (${lastError.name}: ${lastError.message})`
          );
          await new Promise(resolve => setTimeout(resolve, delayMs));
        } else {
          console.error(
            `❌ [${label}] All ${maxAttempts} attempts failed. (${lastError.name}: ${lastError.message})`
          );
        }
      }
    }

    // Should never reach here, but TypeScript needs the throw
    throw lastError ?? new Error(`${label} failed after ${maxAttempts} attempts`);
  }

  // ===== WRITE VERIFICATION =====

  /**
   * Verify a file was successfully written to the provider.
   *
   * Calls provider.list() to confirm file exists with expected size.
   * Throws on verification failure (to be caught by retry logic).
   *
   * @param filename - Name of file to verify
   * @param expectedSize - Expected file size in bytes
   * @throws Error if file not found or size mismatch (when size > 0)
   */
  private async verifyFileWrite(filename: string, expectedSize: number): Promise<void> {
    if (!this.provider) {
      throw new Error('Sync provider not set');
    }

    console.log(`🔍 Verifying ${filename}...`);

    // Note: list() is not retried here. The caller wraps write+verify as a unit
    // in retryWithBackoff(), so list() failures trigger a full retry.
    const files = await this.provider.list();
    const file = files.find(f => f.filename === filename);

    if (!file) {
      throw new Error(`Verification failed: ${filename} not found after write`);
    }

    // Size verification
    if (file.size === 0 && expectedSize === 0) {
      // Expected empty file - verification passed
    } else if (file.size === 0 && expectedSize > 0) {
      // Provider reports 0 unexpectedly - Google Drive edge case, skip size check
      console.warn(
        `⚠️  ${filename} size reported as 0, skipping size verification (Google Drive edge case)`
      );
    } else if (file.size !== expectedSize) {
      throw new Error(
        `Verification failed: ${filename} size mismatch (expected: ${expectedSize}, actual: ${file.size})`
      );
    }

    console.log(`✅ Verified ${filename} (${file.size > 0 ? `${file.size} bytes` : 'exists'})`);
  }

  /**
   * Verify manifest was successfully written to the provider.
   *
   * Calls provider.list() to confirm manifest exists (existence-only check).
   * Throws on verification failure (to be caught by retry logic).
   *
   * @throws Error if manifest not found
   */
  private async verifyManifestWrite(): Promise<void> {
    if (!this.provider) {
      throw new Error('Sync provider not set');
    }

    console.log(`🔍 Verifying ${SYNC_MANIFEST_FILENAME}...`);

    // Note: list() is not retried here. The caller wraps write+verify as a unit
    // in retryWithBackoff(), so list() failures trigger a full retry.
    const files = await this.provider.list();
    const manifest = files.find(f => f.filename === SYNC_MANIFEST_FILENAME);

    if (!manifest) {
      throw new Error(`Verification failed: ${SYNC_MANIFEST_FILENAME} not found after write`);
    }

    console.log(`✅ Verified ${SYNC_MANIFEST_FILENAME} exists`);
  }

  // ===== MANIFEST MANAGEMENT =====

  /**
   * Read sync manifest from provider
   *
   * @returns Sync manifest or null if not found
   */
  private async readManifest(): Promise<SyncManifest | null> {
    if (!this.provider) {
      throw new Error('Sync provider not set');
    }

    try {
      const json = await this.provider.read(SYNC_MANIFEST_FILENAME);
      const manifest: SyncManifest = JSON.parse(json);
      return manifest;
    } catch (_error) {
      // Manifest doesn't exist yet (first sync)
      console.log('No manifest found, will create on first export');
      return null;
    }
  }

  /**
   * Write sync manifest to provider
   *
   * @param manifest - Sync manifest data
   */
  private async writeManifest(manifest: SyncManifest): Promise<void> {
    if (!this.provider) {
      throw new Error('Sync provider not set');
    }

    const json = JSON.stringify(manifest, null, 2);
    await this.provider.write(SYNC_MANIFEST_FILENAME, json);
    console.log('✅ Manifest updated');
  }

  /**
   * Update manifest after exporting baseline
   *
   * Wraps manifest creation + write + verify in retryWithBackoff() for resilience.
   * Uses the baseline's exportedAt timestamp for consistency (avoids drift across retries).
   *
   * @param baselineFilename - Filename of new baseline
   * @param baselineExportedAt - Timestamp from baseline.exportedAt for consistency
   * @throws Error if all retries exhausted
   */
  private async updateManifestAfterBaseline(
    baselineFilename: string,
    baselineExportedAt: string
  ): Promise<void> {
    await this.retryWithBackoff(async () => {
      const manifest: SyncManifest = {
        currentBaseline: baselineFilename,
        deltas: [], // Clear deltas after new baseline
        lastExportedAt: baselineExportedAt,
        baselineCreatedAt: baselineExportedAt,
        totalDeltaCount: 0,
      };

      await this.writeManifest(manifest);
      await this.verifyManifestWrite();
    }, { label: 'manifest replace+verify' });
  }

  /**
   * Update manifest after exporting delta
   *
   * Wraps entire update logic (re-read + build + write + verify) in retry.
   * Each retry re-reads manifest to capture concurrent changes from other devices.
   *
   * @param deltaFilename - Filename of new delta
   * @throws Error if manifest not found or all retries exhausted
   */
  private async updateManifestAfterDelta(deltaFilename: string): Promise<void> {
    await this.retryWithBackoff(async () => {
      // Re-read manifest on each attempt to capture other devices' changes
      const manifest = await this.readManifest();

      if (!manifest) {
        throw new Error('Cannot add delta without baseline - manifest not found');
      }

      // Guard against duplicate entries on retry (if prior write succeeded but verify failed)
      if (manifest.deltas.includes(deltaFilename)) {
        // Delta already present - just verify and return
        await this.verifyManifestWrite();
        return;
      }

      const now = new Date().toISOString();

      const updatedManifest: SyncManifest = {
        ...manifest,
        deltas: [...manifest.deltas, deltaFilename],
        lastExportedAt: now,
        totalDeltaCount: (manifest.totalDeltaCount ?? 0) + 1,
      };

      await this.writeManifest(updatedManifest);
      await this.verifyManifestWrite();
    }, { label: 'manifest update+verify' });
  }

  // ===== IMPORT LOGIC =====

  /**
   * Import baseline (non-destructive merge - preserves local changes)
   *
   * IMPORTANT: This uses the same merge logic as importDelta() to prevent
   * destroying local changes. Previously used table.clear() which would
   * wipe all local data before loading baseline - causing data loss.
   *
   * Uses simple last-write-wins for all tables:
   * - Case 0: Tombstone handling (deletion/resurrection)
   * - Case 1: No local record → add from baseline
   * - Case 2: Local exists, baseline newer → replace with baseline (rare)
   * - Case 3: Local exists, local newer → skip (keep local, will be exported)
   *
   * NOTE: Unlike importDelta(), baseline import NEVER creates conflicts.
   * Baseline is a historical snapshot - differences are pending changes, not conflicts.
   *
   * @param baseline - Baseline export data
   */
  async importBaseline(baseline: BaselineExport): Promise<void> {
    console.log('📥 Importing baseline (non-destructive merge)...');

    for (const tableName of SYNC_TABLES) {
      const remoteRecords = baseline.data[tableName];

      if (!remoteRecords || remoteRecords.length === 0) {
        continue;
      }

      let addedCount = 0;
      let mergedCount = 0;
      let skippedCount = 0;

      try {
        for (const remoteRecord of remoteRecords) {
          const { id } = remoteRecord as { id: string };
          // Type-safe table access with dynamic typing at contract boundary
          const table = getTable(tableName) as {
            get: (id: string) => Promise<unknown>;
            add: (item: never) => Promise<unknown>;
            put: (item: never) => Promise<unknown>;
          };
          const localRecord = await table.get(id);

          // ===== CASE 0: TOMBSTONE HANDLING =====
          const remoteIsTombstoned = isTombstoned(remoteRecord);
          const localIsTombstoned = localRecord && isTombstoned(localRecord);

          // Case 0a: Remote is tombstoned
          if (remoteIsTombstoned) {
            if (!localRecord) {
              // No local record + remote tombstone = add tombstone for audit trail
              await table.add(remoteRecord as never);
              // Notify same-tab UI first, then broadcast to other tabs
              syncCoordinatorService.notifyLocalChange(tableName, id);
              syncCoordinatorService.notifyChange(tableName, id);
              addedCount++;
            } else if (shouldAcceptRemoteDeletion(localRecord, remoteRecord)) {
              // Apply tombstone to local (remote deletion is newer)
              await table.put(remoteRecord as never);
              // Notify same-tab UI first, then broadcast to other tabs
              syncCoordinatorService.notifyLocalChange(tableName, id);
              syncCoordinatorService.notifyChange(tableName, id);
              mergedCount++;
            } else {
              skippedCount++;
            }
            continue;
          }

          // Case 0b: Local is tombstoned, remote is NOT tombstoned
          if (localIsTombstoned) {
            if (!shouldResurrect(localRecord, remoteRecord)) {
              skippedCount++;
              continue; // Keep local tombstone
            }
            // Else: fall through to normal processing (allow resurrection)
          }

          // ===== CASE 1: No local record exists =====
          if (!localRecord) {
            await table.add(remoteRecord as never);
            // Notify same-tab UI first, then broadcast to other tabs
            syncCoordinatorService.notifyLocalChange(tableName, id);
            syncCoordinatorService.notifyChange(tableName, id);
            addedCount++;
            continue;
          }

          // ===== CASE 2 & 3: Local exists - use last-write-wins (NO conflicts for baseline) =====
          // IMPORTANT: Baseline is a historical snapshot, not concurrent data.
          // Differences between baseline and local are NOT conflicts - they're
          // pending changes that will be exported. Conflicts are only created
          // during delta import where we compare concurrent changes from different devices.
          type TimestampedRecord = {
            updatedAt?: string;
            lastModified?: string;
          };
          const localTS =
            (localRecord as TimestampedRecord).updatedAt ||
            (localRecord as TimestampedRecord).lastModified;
          const remoteTS =
            (remoteRecord as TimestampedRecord).updatedAt ||
            (remoteRecord as TimestampedRecord).lastModified;

          if (
            remoteTS &&
            localTS &&
            new Date(remoteTS).getTime() > new Date(localTS).getTime()
          ) {
            // Baseline is newer than local - unusual but possible if device was offline
            // when baseline was created. Replace local with baseline.
            await table.put(remoteRecord as never);
            // Notify same-tab UI first, then broadcast to other tabs
            syncCoordinatorService.notifyLocalChange(tableName, id);
            syncCoordinatorService.notifyChange(tableName, id);
            mergedCount++;
          } else {
            // Local is newer or timestamps equal - keep local (will be exported)
            skippedCount++;
          }
        }

        console.log(
          `  ✅ Baseline ${tableName}: ${addedCount} added, ${mergedCount} merged, ${skippedCount} skipped (local newer)`
        );
      } catch (error) {
        console.error(`Error importing baseline to ${tableName}:`, error);
        throw new Error(`Failed to import baseline to ${tableName}`);
      }
    }

    // ===== MERGE lastAppliedSeqByDevice FROM BASELINE =====
    // Baseline-wins strategy: baseline values override local for overlapping deviceIds,
    // but local-only deviceIds are preserved (baseline may predate those devices).
    const deviceId = getOrCreateDeviceId();
    const baselineSeqMap = baseline.lastAppliedSeqByDevice ?? {}; // v1 compat: missing = empty
    const existingState = await db.sync_state.get(deviceId);
    const localSeqMap = existingState?.lastAppliedSeqByDevice ?? {};
    const mergedSeqMap = { ...localSeqMap, ...baselineSeqMap };

    const now = new Date().toISOString();
    if (existingState) {
      await db.sync_state.put({
        ...existingState,
        lastAppliedSeqByDevice: mergedSeqMap,
        updatedAt: now,
      });
    } else {
      // Create with defaults matching delta import pattern
      await db.sync_state.put({
        id: deviceId,
        createdAt: now,
        updatedAt: now,
        lastSyncedAt: new Date(0).toISOString(),
        lastSyncDirection: 'import',
        pendingChanges: 0,
        syncProvider: 'local',
        lastSeqIssued: 0,
        lastAppliedSeqByDevice: mergedSeqMap,
      });
    }
    console.log('  ✅ Merged lastAppliedSeqByDevice from baseline');

    console.log('✅ Baseline import complete (non-destructive)');
  }

  /**
   * Import delta (apply operations to local database)
   *
   * Processes operations[] array using operation-based sync:
   * - Applies field-level operations (set, delete) to local records
   * - Uses sequence tracking for gap detection and idempotency
   * - Notifies cross-tab coordinator of changes
   *
   * @param delta - Delta export data containing operations to apply
   */
  async importDelta(delta: DeltaExport): Promise<DeltaImportResult> {
    console.log('📥 Importing delta...');
    const deviceId = getOrCreateDeviceId();

    // ===== OPERATION-BASED SYNC (v2) =====
    let gapInfo: GapDetectionResult | undefined;
    let opResult: ProcessOperationsResult | undefined;

    if (delta.operations && delta.operations.length > 0) {
      console.log(`📥 Processing ${delta.operations.length} operations...`);

      // Read lastAppliedSeqByDevice from sync_state
      const syncState = await db.sync_state.get(deviceId);
      const lastAppliedSeqByDevice = syncState?.lastAppliedSeqByDevice ?? {};

      // Defensive fallback for malformed deltas (type says required, but runtime may differ)
      const opSeqRanges = delta.opSeqRanges ?? {};
      if (!delta.opSeqRanges && delta.operations.length > 0) {
        console.warn('[SyncEngine] Delta has operations but missing opSeqRanges - gap detection skipped');
      }

      // Run gap detection only if opSeqRanges is non-empty
      // Empty {} means no pending operations on exporting device - skip gap detection
      if (Object.keys(opSeqRanges).length > 0) {
        gapInfo = detectSequenceGaps(opSeqRanges, lastAppliedSeqByDevice, delta.compactedSeqs);

        // Log gap warnings
        if (gapInfo.hasGaps) {
          for (const gap of gapInfo.gaps) {
            console.warn(
              `[SyncEngine] Sequence gap detected for device ${gap.deviceId.slice(0, 8)}: ` +
                `expected seq ${gap.expectedSeq}, got ${gap.actualMinSeq} (${gap.gapSize} ops missing)`
            );
          }
        }

        // Log stale range warnings
        if (gapInfo.staleRanges.length > 0) {
          for (const stale of gapInfo.staleRanges) {
            console.warn(
              `[SyncEngine] Stale delta range for device ${stale.deviceId.slice(0, 8)}: ` +
                `lastApplied=${stale.lastApplied}, delta maxSeq=${stale.deltaMaxSeq}`
            );
          }
        }
      }
      // else: opSeqRanges is empty {} - no gap detection needed, gapInfo remains undefined

      // Create record fetcher for processOperations
      const recordFetcher = async (
        table: string,
        recordId: string
      ): Promise<SyncRecord | undefined> => {
        if (!SYNC_TABLES.includes(table as SyncTableName)) {
          console.warn(`[SyncEngine] Operation targets unknown table: ${table}`);
          return undefined;
        }
        return getTable(table as SyncTableName).get(recordId) as Promise<SyncRecord | undefined>;
      };

      // Process operations
      opResult = await processOperations(delta.operations, lastAppliedSeqByDevice, recordFetcher);

      console.log('📥 Operation processing:', opResult.stats);

      // Write modified records to database
      // CONTRACT: All operations in delta.operations must target SYNC_TABLES.
      // Operations targeting unknown tables are skipped with warning.
      for (const [recordKey, record] of opResult.modifiedRecords) {
        const tableName = opResult.recordTables.get(recordKey);
        if (!tableName) {
          // This indicates a bug in processOperations - it should always set recordTables
          console.error(
            `[SyncEngine] No table found for record key: ${recordKey} - this is a bug in processOperations`
          );
          continue;
        }

        // Validate table is in SYNC_TABLES before writing
        if (!SYNC_TABLES.includes(tableName as SyncTableName)) {
          console.warn(
            `[SyncEngine] Skipping write for unknown table: ${tableName} (record ${recordKey})`
          );
          continue;
        }

        // Write record to database
        const table = getTable(tableName as SyncTableName);
        await table.put(record as never);

        // Notify same-tab UI first, then broadcast to other tabs
        syncCoordinatorService.notifyLocalChange(tableName, record['id'] as string);
        syncCoordinatorService.notifyChange(tableName, record['id'] as string);
      }

      // Update lastAppliedSeqByDevice in sync_state
      const now = new Date().toISOString();
      const existingState = await db.sync_state.get(deviceId);
      if (existingState) {
        await db.sync_state.put({
          ...existingState,
          lastAppliedSeqByDevice: opResult.updatedLastAppliedSeq,
          updatedAt: now,
        });
      } else {
        // Create with defaults matching SyncOperationService._incrementSeqInTransaction()
        await db.sync_state.put({
          id: deviceId,
          createdAt: now,
          updatedAt: now,
          lastSyncedAt: new Date(0).toISOString(),
          lastSyncDirection: 'import',
          pendingChanges: 0,
          syncProvider: 'local',
          lastSeqIssued: 0,
          lastAppliedSeqByDevice: opResult.updatedLastAppliedSeq,
        });
      }

      console.log(
        `📥 Operations complete: ${opResult.stats.appliedOps} applied, ` +
          `${opResult.stats.skippedIdempotent} skipped (idempotent), ` +
          `${opResult.stats.skippedLWW} skipped (LWW)`
      );
    }
    // ===== END OPERATION-BASED SYNC =====
    // NOTE: State-based sync (changes[] processing) removed in T11 Phase 3.
    // All delta imports are now operation-only. Baseline imports remain state-based.

    console.log('✅ Delta import complete');

    // Build result with only defined properties (exactOptionalPropertyTypes compliance)
    const result: DeltaImportResult = {};
    if (gapInfo) {
      result.gapInfo = gapInfo;
    }
    if (opResult?.stats) {
      result.operationStats = opResult.stats;
    }
    return result;
  }

  // ===== BASELINE COMPACTION =====

  /**
   * Check if baseline compaction should be triggered
   *
   * Triggers on:
   * - 30 deltas accumulated
   * - 90 days since last baseline
   * - Delta size > 50% of baseline size
   *
   * @param manifest - Current sync manifest
   * @returns True if compaction should be triggered
   */
  private async shouldCreateBaseline(
    manifest: SyncManifest | null
  ): Promise<boolean> {
    // First sync - always create baseline
    if (!manifest) {
      return true;
    }

    // Check delta count threshold
    if (manifest.deltas.length >= MAX_DELTAS_BEFORE_COMPACTION) {
      console.log(
        `Baseline compaction triggered: ${manifest.deltas.length} deltas accumulated`
      );
      return true;
    }

    // Check age threshold
    if (manifest.baselineCreatedAt) {
      const baselineAge =
        Date.now() - new Date(manifest.baselineCreatedAt).getTime();
      const daysOld = baselineAge / (1000 * 60 * 60 * 24);

      if (daysOld >= MAX_DAYS_BETWEEN_BASELINES) {
        console.log(
          `Baseline compaction triggered: ${Math.round(daysOld)} days old`
        );
        return true;
      }
    }

    // Check delta size threshold (requires provider to get file sizes)
    // TODO: Implement size comparison when SyncProvider.list() includes size
    // For now, skip this check

    return false;
  }

  /**
   * Create new baseline from current state
   *
   * Reads manifest, reconstructs current state (baseline + deltas),
   * exports as new baseline, updates manifest, and cleans up old baselines.
   */
  async createNewBaseline(): Promise<void> {
    if (!this.provider) {
      throw new Error('Sync provider not set');
    }

    console.log('🔄 Creating new baseline (compaction)...');

    // Archive current state before compaction (baseline + all deltas + manifest)
    console.log('📦 Archiving state before compaction...');
    if ('archiveCurrentState' in this.provider && typeof this.provider.archiveCurrentState === 'function') {
      const archiveName = await this.provider.archiveCurrentState();
      if (archiveName) {
        console.log(`✅ State archived to: archive/${archiveName}/`);
      } else {
        console.warn('⚠️ Failed to archive state (continuing anyway)');
      }
    }

    // Export current state as new baseline
    const baseline = await this.exportBaseline();
    const filename = await this.writeBaseline(baseline);

    // Update manifest (clears deltas)
    try {
      await this.updateManifestAfterBaseline(filename, baseline.exportedAt);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      console.warn(`⚠️ Manifest update failed after baseline write - orphaned file may exist: ${filename} (${msg})`);
      throw error;
    }

    // Clean up old archived states (keep 6 most recent)
    await this.cleanupOldArchives();

    console.log('✅ Baseline compaction complete');
  }

  /**
   * Force rebaseline from local data (recovery for corrupted cloud state)
   *
   * Used when cloud data is corrupted (SCHEMA_VERSION_MISSING, INVALID, MISMATCH).
   * Skips import entirely and overwrites cloud with fresh baseline from local data.
   *
   * **Key differences from createNewBaseline()**:
   * - Skips import step (cloud data is untrusted)
   * - Does NOT clean up old archives (preserve corrupted state for debugging)
   *
   * @returns SyncResult with success status and baseline details
   */
  async forceRebaselineFromLocal(): Promise<SyncResult> {
    if (!this.provider) {
      return {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: 'Sync provider not set',
      };
    }

    console.log('🔄 Force rebaseline from local (recovery mode)...');

    try {
      // Step 1: Archive corrupted cloud state for debugging
      console.log('📦 Archiving corrupted cloud state...');
      if ('archiveCurrentState' in this.provider && typeof this.provider.archiveCurrentState === 'function') {
        const archiveName = await this.provider.archiveCurrentState();
        if (archiveName) {
          console.log(`✅ Corrupted state archived to: archive/${archiveName}/`);
        } else {
          console.warn('⚠️ Failed to archive corrupted state (continuing anyway)');
        }
      }

      // Step 2: Export local data as fresh baseline (reuse existing helpers)
      const baseline = await this.exportBaseline();
      const filename = await this.writeBaseline(baseline);

      // Step 3: Update manifest (clears all delta refs, updates lastSyncedAt)
      try {
        await this.updateManifestAfterBaseline(filename, baseline.exportedAt);
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        console.warn(`⚠️ Manifest update failed after baseline write - orphaned file may exist: ${filename} (${msg})`);
        throw error;
      }

      // Step 4: Update local sync_state to keep IndexedDB coherent with cloud manifest
      // Use partial update to preserve lastAppliedSeqByDevice, lastSeqIssued, etc.
      await updateSyncStatePartial(this.deviceId, {
        lastSyncedAt: baseline.exportedAt,
        lastSyncDirection: 'export',
        pendingChanges: 0,
        syncProvider: this.provider.name,
        lastSyncedDeltaFilename: filename, // Bookmark to new baseline
      });

      // NOTE: Do NOT call cleanupOldArchives() - preserve corrupted archives for debugging

      console.log('✅ Force rebaseline complete - cloud data overwritten with local');

      return {
        success: true,
        type: 'baseline',
        filename,
        dataExportedAt: baseline.exportedAt,
        recordCount: Object.values(baseline.data).reduce((sum, records) => sum + records.length, 0),
        tableCount: Object.keys(baseline.data).length,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error during rebaseline';
      console.error('❌ Force rebaseline failed:', errorMessage);

      return {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: errorMessage,
      };
    }
  }

  /**
   * Clean up old archived states
   *
   * Retains 6 most recent archives, deletes archives older than 2 years.
   * Archives are stored in /archive/YYYY-MM-DD-HH-MM-SS/ subdirectories.
   */
  private async cleanupOldArchives(): Promise<void> {
    if (!this.provider) {
      throw new Error('Sync provider not set');
    }

    // Only LocalDirectorySyncProvider supports directory listing
    if (!('archiveCurrentState' in this.provider)) {
      return; // Skip cleanup for providers that don't support archives
    }

    try {
      // Note: We can't directly list subdirectories with the current SyncProvider interface
      // This would require extending the provider interface to support directory operations
      // For now, we'll implement a basic cleanup that can be enhanced later

      console.log('📦 Archive cleanup: Checking for old archives...');
      // TODO: Implement full directory-based cleanup when provider supports it
      // Current limitation: SyncProvider.list() only returns files, not subdirectories
      // Archives will accumulate but won't cause issues (just take up space)

    } catch (error) {
      console.error('Error cleaning up old archives:', error);
      // Non-fatal - continue
    }
  }

  // ===== HIGH-LEVEL SYNC OPERATIONS =====

  /**
   * Peek at the last export file to determine which device made the most recent export.
   * Used to skip import when this device was the last exporter (nothing new to pull).
   *
   * Also returns the parsed latest delta for use in additional skip checks
   * (e.g., checking if all operations are already applied via opSeqRanges).
   *
   * @param manifest - The sync manifest
   * @returns Object with deviceId of last exporter and latestDelta (both nullable on error)
   */
  private async getLastExporterInfo(manifest: SyncManifest): Promise<{
    deviceId: string | null;
    latestDelta: DeltaExport | null;
  }> {
    if (!this.provider) {
      return { deviceId: null, latestDelta: null };
    }

    try {
      // If deltas exist, check the LAST delta (most recent export)
      if (manifest.deltas.length > 0) {
        const lastDeltaFilename = manifest.deltas[manifest.deltas.length - 1];
        if (!lastDeltaFilename) {
          return { deviceId: null, latestDelta: null };
        }
        const deltaJson = await this.provider.read(lastDeltaFilename);
        const delta: DeltaExport = JSON.parse(deltaJson);
        if (isDeltaExport(delta)) {
          return { deviceId: delta.deviceId, latestDelta: delta };
        }
      }

      // No deltas - check baseline (no latestDelta to return)
      const baselineJson = await this.provider.read(manifest.currentBaseline);
      const baseline: BaselineExport = JSON.parse(baselineJson);
      if (isBaselineExport(baseline)) {
        return { deviceId: baseline.deviceId, latestDelta: null };
      }

      return { deviceId: null, latestDelta: null };
    } catch (error) {
      console.warn('⚠️ Unable to determine last exporter deviceId:', error);
      return { deviceId: null, latestDelta: null };
    }
  }

  /**
   * Internal import logic - assumes lock is already held
   *
   * @returns Import result with success status, manifest presence, skip status, and schema error
   */
  private async _doImport(): Promise<{
    success: boolean;
    hadManifest: boolean;
    skipped?: boolean;
    error?: string;
    schemaVersionError?: SchemaVersionError;
    manifest?: SyncManifest | null; // Pass to export to avoid redundant download
  }> {
    if (!this.provider) {
      return { success: false, hadManifest: false, error: 'Sync provider not set' };
    }

    console.log('📥 Starting import (pull)...');

    // Read manifest
    const manifest = await this.readManifest();

    if (!manifest) {
      console.log('ℹ️  No manifest found - nothing to import (first sync)');
      return { success: true, hadManifest: false, manifest: null };
    }

    // ===== PREFLIGHT: Capture manifest snapshot for consistency check =====
    // Store key values to detect concurrent changes during import
    const manifestSnapshot: ManifestSnapshot = {
      lastExportedAt: manifest.lastExportedAt,
      currentBaseline: manifest.currentBaseline,
      deltaCount: manifest.deltas.length,
      lastDeltaFilename: manifest.deltas[manifest.deltas.length - 1] ?? null,
    };

    // Get the latest sync filename from manifest (latest delta, or baseline if no deltas)
    // This serves as a "bookmark" to detect if anything has changed since last sync
    // A valid manifest always has currentBaseline, so this is always defined
    const lastDelta = manifest.deltas[manifest.deltas.length - 1];
    const latestSyncFilename: string = lastDelta ?? manifest.currentBaseline;

    // Check if manifest is unchanged since last sync (filename-based skip)
    // This must come BEFORE downloading any files for efficiency
    const syncState = await db.sync_state.get(this.deviceId);
    if (syncState?.lastSyncedDeltaFilename === latestSyncFilename) {
      console.log(
        `⏭️  Skipping import - manifest unchanged (lastSynced: ${latestSyncFilename})`
      );

      // Update sync_state for consistency (preserves existing fields)
      await updateSyncStatePartial(this.deviceId, {
        lastSyncedAt: manifest.lastExportedAt,
        lastSyncDirection: 'import',
        pendingChanges: 0,
        syncProvider: this.provider?.name ?? 'local',
        lastSyncedDeltaFilename: latestSyncFilename,
      });

      return { success: true, hadManifest: true, skipped: true, manifest };
    }

    // Check if this device was the last exporter - if so, skip import (nothing new)
    const { deviceId: lastExporterDeviceId } = await this.getLastExporterInfo(manifest);
    if (lastExporterDeviceId === this.deviceId) {
      console.log(
        `⏭️  Skipping import - this device (${this.deviceId.slice(0, 8)}) was the last exporter, no new remote data`
      );

      // Update sync_state for consistency (preserves existing fields)
      await updateSyncStatePartial(this.deviceId, {
        lastSyncedAt: manifest.lastExportedAt,
        lastSyncDirection: 'import',
        pendingChanges: 0,
        syncProvider: this.provider?.name ?? 'local',
        lastSyncedDeltaFilename: latestSyncFilename,
      });

      return { success: true, hadManifest: true, skipped: true, manifest };
    }

    // ===== Parse all artifacts upfront =====
    // Parse baseline and ALL deltas before any validation or writes
    // This allows schema version validation across the entire batch

    console.log('📥 Reading and parsing artifacts...');

    // Parse baseline
    const baselineJson = await this.provider.read(manifest.currentBaseline);
    let baseline: BaselineExport;
    try {
      baseline = JSON.parse(baselineJson);
    } catch (parseError) {
      console.error('[SyncEngine] Failed to parse baseline JSON', {
        filename: manifest.currentBaseline,
        error: parseError instanceof Error ? parseError.message : 'Unknown parse error',
      });
      return { success: false, hadManifest: true, error: `Invalid baseline file: malformed JSON` };
    }

    if (!isBaselineExport(baseline)) {
      return { success: false, hadManifest: true, error: 'Invalid baseline file' };
    }

    // Parse ALL deltas (including same-device ones for validation)
    const parsedDeltas: ParsedDelta[] = [];
    for (const deltaFilename of manifest.deltas) {
      const deltaJson = await this.provider.read(deltaFilename);
      let delta: DeltaExport;
      try {
        delta = JSON.parse(deltaJson);
      } catch (parseError) {
        console.error('[SyncEngine] Failed to parse delta JSON', {
          filename: deltaFilename,
          error: parseError instanceof Error ? parseError.message : 'Unknown parse error',
        });
        return { success: false, hadManifest: true, error: `Invalid delta file: ${deltaFilename} (malformed JSON)` };
      }

      if (!isDeltaExport(delta)) {
        return { success: false, hadManifest: true, error: `Invalid delta file: ${deltaFilename}` };
      }

      parsedDeltas.push({ delta, filename: deltaFilename });
    }

    // ===== PREFLIGHT: Collect operations from all parsed deltas =====
    // Aggregate all operations for global gap detection and batch validation
    const collectedOperations: CollectedOperations = {
      operations: [],
      byDeviceOps: new Map(),
      opSeqRanges: {},
      compactedSeqs: {},
      remoteDevices: new Set(),
      localDeviceId: this.deviceId,
      gappedDevices: new Set(),
    };

    for (const { delta } of parsedDeltas) {
      // Collect all operations (including same-device for complete gap detection)
      if (delta.operations && delta.operations.length > 0) {
        collectedOperations.operations.push(...delta.operations);

        // Group by device for gap filtering
        for (const op of delta.operations) {
          const deviceOps = collectedOperations.byDeviceOps.get(op.deviceId);
          if (deviceOps) {
            deviceOps.push(op);
          } else {
            collectedOperations.byDeviceOps.set(op.deviceId, [op]);
          }
        }
      }

      // Merge opSeqRanges (take max of maxSeq, min of minSeq per device)
      for (const [devId, range] of Object.entries(delta.opSeqRanges)) {
        const existing = collectedOperations.opSeqRanges[devId];
        if (existing) {
          existing.minSeq = Math.min(existing.minSeq, range.minSeq);
          existing.maxSeq = Math.max(existing.maxSeq, range.maxSeq);
        } else {
          collectedOperations.opSeqRanges[devId] = { ...range };
        }
      }

      // Merge compactedSeqs - expand and merge into Set per device (normalized format)
      if (delta.compactedSeqs) {
        for (const [devId, seqs] of Object.entries(delta.compactedSeqs)) {
          const existing = collectedOperations.compactedSeqs[devId] ?? new Set<number>();
          const expanded = expandCompactedSeqs(seqs);
          for (const seq of expanded) {
            existing.add(seq);
          }
          collectedOperations.compactedSeqs[devId] = existing;
        }
      }

    }

    // Derive remoteDevices from actual operation device IDs.
    // ASSUMPTION: Devices that only appear in compactedSeqs (no operations) don't need
    // gap checking - they have no operations to import, only evidence of previously-processed seqs.
    // If this assumption changes (e.g., compactedSeqs used for other purposes), revisit this.
    collectedOperations.remoteDevices.clear();
    for (const deviceId of collectedOperations.byDeviceOps.keys()) {
      if (deviceId !== this.deviceId) {
        collectedOperations.remoteDevices.add(deviceId);
      }
    }

    console.log(
      `📥 Preflight collection: ${collectedOperations.operations.length} ops from ` +
      `${collectedOperations.remoteDevices.size} remote device(s)`
    );

    // ===== Gap Detection & Device Filtering =====
    // Merge baseline's lastAppliedSeqByDevice with local for accurate gap detection.
    // Baseline may have newer seq tracking (e.g., from another device that compacted).
    // Using local-only would falsely flag gaps for seqs already in baseline.
    const gapDetectionSyncState = await db.sync_state.get(this.deviceId);
    const localSeqByDevice = gapDetectionSyncState?.lastAppliedSeqByDevice ?? {};
    const baselineSeqByDevice = baseline?.lastAppliedSeqByDevice ?? {};
    const mergedSeqByDevice: Record<string, number> = { ...localSeqByDevice };
    for (const [devId, seq] of Object.entries(baselineSeqByDevice)) {
      mergedSeqByDevice[devId] = Math.max(localSeqByDevice[devId] ?? 0, seq);
    }

    // Run gap detection for remote devices only (local device ops are skipped at apply time)
    for (const deviceId of collectedOperations.remoteDevices) {
      const deviceOps = collectedOperations.byDeviceOps.get(deviceId) ?? [];
      const deviceCompacted = collectedOperations.compactedSeqs[deviceId] ?? new Set();
      const lastApplied = mergedSeqByDevice[deviceId] ?? 0;

      const gapResult = detectDeviceGaps(deviceOps, deviceCompacted, lastApplied);

      if (gapResult.hasGaps) {
        collectedOperations.gappedDevices.add(deviceId);
        // Log gap range using min/max/count (memory-optimized)
        console.warn(
          `⚠️ [SyncEngine] Skipping device ${deviceId.slice(0, 8)} due to sequence gaps: ` +
          `seqs ${gapResult.minMissing}-${gapResult.maxMissing} missing (${gapResult.count} total). ` +
          `Device will retry when missing deltas arrive.`
        );
      }
    }

    // Filter operations from gapped devices
    if (collectedOperations.gappedDevices.size > 0) {
      // Filter operations array
      collectedOperations.operations = collectedOperations.operations.filter(
        op => !collectedOperations.gappedDevices.has(op.deviceId)
      );

      // Remove gapped devices from byDeviceOps, remoteDevices, opSeqRanges, and compactedSeqs
      for (const gappedDeviceId of collectedOperations.gappedDevices) {
        collectedOperations.byDeviceOps.delete(gappedDeviceId);
        collectedOperations.remoteDevices.delete(gappedDeviceId);
        delete collectedOperations.opSeqRanges[gappedDeviceId];
        delete collectedOperations.compactedSeqs[gappedDeviceId];
      }

      console.log(
        `📥 Gap filtering complete: ${collectedOperations.gappedDevices.size} device(s) excluded, ` +
        `${collectedOperations.remoteDevices.size} device(s) proceeding with ` +
        `${collectedOperations.operations.length} ops`
      );
    }

    // ===== Batch Validation =====
    // Defensive guard: skip batches that span the stale boundary (corrupted lastAppliedSeqByDevice)
    const incompleteBatchOps = validateBatches(
      collectedOperations.operations,
      mergedSeqByDevice,
      this.deviceId // Exclude local device ops (they're never applied anyway)
    );

    if (incompleteBatchOps.size > 0) {
      // Filter out incomplete batch ops
      collectedOperations.operations = collectedOperations.operations.filter(
        (op) => !incompleteBatchOps.has(op)
      );

      // Rebuild byDeviceOps to stay in sync
      collectedOperations.byDeviceOps.clear();
      for (const op of collectedOperations.operations) {
        const existing = collectedOperations.byDeviceOps.get(op.deviceId);
        if (existing) {
          existing.push(op);
        } else {
          collectedOperations.byDeviceOps.set(op.deviceId, [op]);
        }
      }

      console.log(
        `📥 Batch validation: ${incompleteBatchOps.size} ops from incomplete batches excluded`
      );
    }

    // ===== Validate schema versions =====
    // Check all artifacts BEFORE any writes occur

    const schemaVersionError = validateSchemaVersions(
      baseline,
      manifest.currentBaseline,
      parsedDeltas,
      SYNC_SCHEMA_VERSION
    );

    if (schemaVersionError) {
      // Return early - no writes have occurred
      return {
        success: false,
        hadManifest: true,
        error: schemaVersionError.message,
        schemaVersionError,
      };
    }

    // ===== Import validated artifacts =====
    // Schema versions validated - safe to proceed with atomic import
    // All writes wrapped in single transaction for rollback on any failure

    console.log('📥 Starting atomic import...');

    // Capture values before transaction (TypeScript doesn't track nullability through async callbacks)
    const providerName = this.provider.name;
    const deviceId = this.deviceId;

    // ===== MANIFEST CONSISTENCY CHECK (pre-transaction) =====
    // Re-read manifest BEFORE transaction to avoid external I/O inside transaction
    // (GPT feedback: external async inside Dexie transaction can cause idle/expired issues)
    const currentManifest = await this.readManifest();

    if (!currentManifest) {
      // Manifest was deleted during preflight - abort
      console.error('[SyncEngine] Manifest deleted during preflight');
      return {
        success: false,
        hadManifest: true,
        error: `Manifest deleted during import. Snapshot: ${JSON.stringify(manifestSnapshot)}`,
      };
    }

    // Compare all snapshot values to detect any change
    const currentSnapshot: ManifestSnapshot = {
      lastExportedAt: currentManifest.lastExportedAt,
      currentBaseline: currentManifest.currentBaseline,
      deltaCount: currentManifest.deltas.length,
      lastDeltaFilename: currentManifest.deltas[currentManifest.deltas.length - 1] ?? null,
    };

    const snapshotChanged =
      manifestSnapshot.lastExportedAt !== currentSnapshot.lastExportedAt ||
      manifestSnapshot.currentBaseline !== currentSnapshot.currentBaseline ||
      manifestSnapshot.deltaCount !== currentSnapshot.deltaCount ||
      manifestSnapshot.lastDeltaFilename !== currentSnapshot.lastDeltaFilename;

    if (snapshotChanged) {
      // Manifest changed during preflight - abort and let caller retry
      console.warn('[SyncEngine] Manifest changed during preflight, aborting import');
      return {
        success: false,
        hadManifest: true,
        error:
          `Manifest changed during import. ` +
          `Snapshot: lastExportedAt=${manifestSnapshot.lastExportedAt}, ` +
          `baseline=${manifestSnapshot.currentBaseline}, deltas=${manifestSnapshot.deltaCount}, ` +
          `lastDelta=${manifestSnapshot.lastDeltaFilename}. ` +
          `Current: lastExportedAt=${currentSnapshot.lastExportedAt}, ` +
          `baseline=${currentSnapshot.currentBaseline}, deltas=${currentSnapshot.deltaCount}, ` +
          `lastDelta=${currentSnapshot.lastDeltaFilename}.`,
      };
    }

    try {
      // Build transaction scope: all sync tables + sync_state
      const syncTables = getSyncTableInstances();
      const allTables = [...syncTables, db.sync_state];

      await db.transaction('rw', allTables, async () => {
        // Mark transaction as import context FIRST - all nested code will inherit this flag.
        // This suppresses operation logging in SyncOperationService to prevent remote
        // operations from being re-logged as local operations during import.
        markAsImportContext();

        if (!import.meta.env.PROD) {
          console.debug('[SyncEngine] Import context active - op logging suppressed');
        }

        // Import baseline
        await this.importBaseline(baseline);

        // Define record fetcher inside transaction for fresh reads
        const recordFetcher = async (
          table: string,
          recordId: string
        ): Promise<SyncRecord | undefined> => {
          if (!SYNC_TABLES.includes(table as SyncTableName)) {
            console.warn(`[SyncEngine] Operation targets unknown table: ${table}`);
            return undefined;
          }
          return getTable(table as SyncTableName).get(recordId) as Promise<SyncRecord | undefined>;
        };

        // Read lastAppliedSeqByDevice inside transaction before processing
        const preSyncState = await db.sync_state.get(deviceId);
        const lastAppliedSeqByDevice = preSyncState?.lastAppliedSeqByDevice ?? {};

        // Filter out same-device ops before processing
        // (collected for gap detection but must not be re-applied)
        const opsToProcess = collectedOperations.operations.filter(
          op => op.deviceId !== deviceId
        );

        // Process all filtered operations together (not per-delta)
        // Operations have been filtered to exclude:
        // - Same-device ops (filtered above - we don't re-apply our own ops)
        // - Gapped-device ops (filtered during gap detection)
        let opResult: ProcessOperationsResult | undefined;

        // Validate baseline floor timestamp once before processing
        const rawFloor = manifest.baselineCreatedAt ?? baseline.exportedAt;
        const baselineFloorTimestamp = isCanonicalIso(rawFloor) ? rawFloor : undefined;
        if (rawFloor && !baselineFloorTimestamp) {
          console.warn('[SyncEngine] Invalid baseline floor timestamp, skipping stale check', {
            rawFloor,
          });
        }

        if (opsToProcess.length > 0) {
          console.log(`📥 Processing ${opsToProcess.length} operations...`);

          opResult = await processOperations(
            opsToProcess,
            lastAppliedSeqByDevice,
            recordFetcher,
            baselineFloorTimestamp ? { baselineFloorTimestamp } : undefined
          );

          console.log('📥 Operation processing:', opResult.stats);

          // Write modified records to database and notify
          for (const [recordKey, record] of opResult.modifiedRecords) {
            const tableName = opResult.recordTables.get(recordKey);
            if (!tableName || !SYNC_TABLES.includes(tableName as SyncTableName)) {
              console.warn(`[SyncEngine] Skipping write for unknown/invalid table: ${tableName}`);
              continue;
            }

            const table = getTable(tableName as SyncTableName);
            await table.put(record as never);

            // Notify same-tab UI first, then broadcast to other tabs
            syncCoordinatorService.notifyLocalChange(tableName, record['id'] as string);
            syncCoordinatorService.notifyChange(tableName, record['id'] as string);
          }

          console.log(
            `📥 Operations complete: ${opResult.stats.appliedOps} applied, ` +
              `${opResult.stats.skippedIdempotent} skipped (idempotent), ` +
              `${opResult.stats.skippedLWW} skipped (LWW)`
          );
        } else {
          console.log('📥 No operations to process (all filtered or empty)');
        }

        // Calculate contiguous seq per device (defensive: don't advance past gaps)
        // Derive device set from actual processed ops (not remoteDevices - may be stale after batch validation)
        const contiguousSeqByDevice: Record<string, number> = {};

        if (opsToProcess.length > 0 && opResult) {
          const processedDevices = new Set(opsToProcess.map(op => op.deviceId));

          for (const processedDeviceId of processedDevices) {
            const deviceCompacted = collectedOperations.compactedSeqs[processedDeviceId] ?? new Set();
            const lastApplied = lastAppliedSeqByDevice[processedDeviceId] ?? 0;
            const maxSeq = opResult.updatedLastAppliedSeq[processedDeviceId] ?? lastApplied;

            // Build seen seqs from processed ops + compacted
            const seenSeqs = new Set(deviceCompacted);
            for (const op of opsToProcess) {
              if (op.deviceId === processedDeviceId) {
                seenSeqs.add(op.seq);
              }
            }

            contiguousSeqByDevice[processedDeviceId] = calculateContiguousSeq(lastApplied, seenSeqs, maxSeq);

            // Log warning if contiguous differs from max-seen (should never happen in healthy system)
            const contiguous = contiguousSeqByDevice[processedDeviceId];
            const maxSeen = opResult.updatedLastAppliedSeq[processedDeviceId];
            if (contiguous !== undefined && maxSeen !== undefined && contiguous !== maxSeen) {
              console.warn(
                `⚠️ [SyncEngine] Contiguous seq differs from max-seen for device ${processedDeviceId.slice(0, 8)}: ` +
                  `contiguous=${contiguous}, max-seen=${maxSeen}`
              );
            }
          }
        }

        // Update sync state (inside transaction for atomicity)
        // Must preserve existing fields like lastSeqIssued
        const now = new Date().toISOString();
        const existingSyncState = await db.sync_state.get(deviceId);
        await db.sync_state.put({
          ...existingSyncState,
          id: deviceId,
          createdAt: existingSyncState?.createdAt ?? now,
          updatedAt: now,
          lastSyncedAt: manifest.lastExportedAt,
          lastSyncDirection: 'import',
          pendingChanges: 0,
          syncProvider: providerName,
          lastSyncedDeltaFilename: latestSyncFilename,
          // Merge contiguous seq tracking with existing (don't drop devices not in current import)
          // Only merge if we actually processed ops; otherwise preserve existing unchanged
          lastAppliedSeqByDevice:
            Object.keys(contiguousSeqByDevice).length > 0
              ? {
                  ...(existingSyncState?.lastAppliedSeqByDevice ?? {}),
                  ...contiguousSeqByDevice,
                }
              : existingSyncState?.lastAppliedSeqByDevice ?? {},
        });
      });

      console.log(`✅ Atomic import complete [device: ${this.deviceId.slice(0, 8)}]`);
      return { success: true, hadManifest: true, manifest };
    } catch (error) {
      // Transaction failed - all changes automatically rolled back by Dexie
      const errorMessage = error instanceof Error ? error.message : 'Unknown transaction error';
      const errorType = error instanceof Error ? error.name : 'UnknownError';
      console.error('[SyncEngine] Atomic import failed - all changes rolled back', {
        error: errorMessage,
        errorType,
        deviceId: this.deviceId.slice(0, 8),
      });
      return {
        success: false,
        hadManifest: true,
        error: `Import transaction failed: ${errorMessage}`,
      };
    }
  }

  /**
   * Internal export logic - assumes lock is already held
   *
   * @param forceBaseline - If true, always create baseline (skip delta logic)
   * @param cachedManifest - Optional manifest from import to avoid redundant download
   * @returns Detailed sync result with filename, type, and record counts
   */
  private async _doExport(
    forceBaseline = false,
    cachedManifest?: SyncManifest | null
  ): Promise<SyncResult> {
    if (!this.provider) {
      return {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: 'Sync provider not set',
      };
    }

    console.log('📤 Starting export (push)...');

    // Get current sync state
    let syncState = await db.sync_state.get(this.deviceId);

    // Initialize sync state if first sync
    if (!syncState) {
      const now = new Date().toISOString();
      syncState = {
        id: this.deviceId,
        createdAt: now,
        updatedAt: now,
        lastSyncedAt: new Date(0).toISOString(), // Epoch = sync everything
        lastSyncDirection: 'export',
        pendingChanges: 0,
        syncProvider: this.provider.name,
      };
      await db.sync_state.add(syncState);
    }

    // Use cached manifest from import if available, else read fresh
    // Note: updateManifestAfterDelta() still re-reads for multi-device safety
    const manifest = cachedManifest !== undefined ? cachedManifest : await this.readManifest();
    if (cachedManifest !== undefined) {
      console.log('📋 Using cached manifest from import');
    }

    // Determine if baseline compaction needed
    const needsBaseline = forceBaseline || await this.shouldCreateBaseline(manifest);

    let result: SyncResult;

    if (needsBaseline) {
      // Create new baseline
      const baseline = await this.exportBaseline();
      const filename = await this.writeBaseline(baseline);

      // Count records and tables
      let recordCount = 0;
      const tableCount = Object.keys(baseline.data).length;
      for (const records of Object.values(baseline.data)) {
        recordCount += (records as unknown[]).length;
      }

      // Update manifest
      try {
        await this.updateManifestAfterBaseline(filename, baseline.exportedAt);
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        console.warn(`⚠️ Manifest update failed after baseline write - orphaned file may exist: ${filename} (${msg})`);
        throw error;
      }

      result = {
        success: true,
        type: 'baseline',
        filename,
        dataExportedAt: baseline.exportedAt,
        recordCount,
        tableCount,
      };
    } else {
      // Export delta (with compaction)
      const { delta, originalOpIds } = await this.exportDelta(syncState.lastSyncedAt);

      // Count operations and unique tables affected
      const operationCount = delta.operations.length;
      const hasOperations = operationCount > 0;
      const tableCount = hasOperations
        ? new Set(delta.operations.map((op) => op.table)).size
        : 0;

      // Write delta if there are operations to export
      if (hasOperations) {
        // Inline delta write with verification and retry
        const json = JSON.stringify(delta, null, 2);
        const expectedSize = new TextEncoder().encode(json).length;
        const filename = generateDeltaFilename(delta.exportedAt);

        // Write + verify as atomic unit with retry
        await this.retryWithBackoff(async () => {
          if (!this.provider) {
            throw new Error('Sync provider not set');
          }
          await this.provider.write(filename, json);
          await this.verifyFileWrite(filename, expectedSize);
        }, { label: 'delta write+verify' });
        console.log(`✅ Delta written and verified: ${filename}`);

        // Update manifest (includes verification with retry)
        await this.updateManifestAfterDelta(filename);

        // Mark and purge exported operations (two-phase lifecycle)
        // Split try/catch for partial-success visibility: if mark succeeds but purge fails,
        // we log the mark count so diagnostics show ops won't re-export
        if (originalOpIds.length > 0) {
          let markedCount = 0;
          try {
            markedCount = await getSyncOperationService().markOpsSynced(originalOpIds);
          } catch (error) {
            // Mark failed - ops will re-export next sync (safe due to idempotency)
            console.warn(
              `⚠️  Failed to mark operations as synced: ${error instanceof Error ? error.message : String(error)}`
            );
          }

          if (markedCount > 0) {
            try {
              const purgedCount = await getSyncOperationService().purgeOps(originalOpIds);
              console.log(
                `ℹ️  Marked ${markedCount} operations as synced, purged ${purgedCount} operations`
              );
            } catch (error) {
              // Purge failed but mark succeeded - ops won't re-export, just taking up space
              console.warn(
                `⚠️  Marked ${markedCount} operations but failed to purge: ${error instanceof Error ? error.message : String(error)}`
              );
            }
          }
        }

        result = {
          success: true,
          type: 'delta',
          filename,
          dataExportedAt: delta.exportedAt,
          recordCount: operationCount, // Operations count (delta sync is ops-only)
          tableCount,
        };
      } else {
        // No operations to sync - no data exported
        console.log('ℹ️  No operations to sync');
        result = {
          success: true,
          type: 'delta',
          filename: '',
          dataExportedAt: '', // Empty - no data was actually exported
          recordCount: 0,
          tableCount: 0,
        };
      }
    }

    // Update sync state
    const now = new Date().toISOString();
    await db.sync_state.update(this.deviceId, {
      lastSyncedAt: now,
      lastSyncDirection: 'export',
      pendingChanges: 0,
      updatedAt: now,
    });

    console.log('✅ Export complete');
    return result;
  }

  /**
   * Perform full sync operation (export only)
   *
   * Detects changes, exports baseline or delta, updates manifest and sync state.
   * For bidirectional sync (import + export), use bidirectionalSync() instead.
   *
   * @param forceBaseline - If true, always create baseline (skip delta logic)
   * @returns Detailed sync result with filename, type, and record counts
   */
  async sync(forceBaseline = false): Promise<SyncResult> {
    if (!this.provider) {
      throw new Error('Sync provider not set');
    }

    // Check if provider is connected
    const isConnected = await this.provider.isConnected();
    if (!isConnected) {
      console.error('Sync provider not connected');
      return {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: 'Sync provider not connected',
      };
    }

    // Acquire sync lock (cross-tab mutex with TTL)
    const lockAcquired = await syncCoordinatorService.tryAcquireExportLock();
    if (!lockAcquired) {
      console.log('Sync skipped - another tab is syncing');
      return {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: 'Sync locked by another tab',
      };
    }

    try {
      console.log('🔄 Starting sync (export only)...');
      return await this._doExport(forceBaseline);
    } catch (error) {
      console.error('❌ Sync failed:', error);
      return {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        recordCount: 0,
        tableCount: 0,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    } finally {
      // Always release lock (cross-tab mutex)
      syncCoordinatorService.releaseExportLock();
    }
  }

  /**
   * Perform bidirectional sync (import then export)
   *
   * Pull-then-push strategy:
   * 1. Import from provider (merge cloud data with local)
   * 2. Export to provider (push local changes to cloud)
   *
   * This ensures cross-device changes are merged before pushing local state.
   *
   * @param forceBaseline - If true, always create baseline on export
   * @returns Detailed sync result (aligned with export outcome)
   */
  async bidirectionalSync(forceBaseline = false): Promise<SyncResult> {
    if (!this.provider) {
      throw new Error('Sync provider not set');
    }

    // Acquire sync lock ONCE for entire bidirectional operation
    // Note: Connection validation happens via ensureValidToken() in first provider operation
    const lockAcquired = await syncCoordinatorService.tryAcquireExportLock();
    if (!lockAcquired) {
      console.log('Sync skipped - another tab is syncing');
      return {
        success: false,
        type: 'baseline',
        filename: '',
        dataExportedAt: '',
        dataImportedAt: '',
        importOccurred: false,
        recordCount: 0,
        tableCount: 0,
        error: 'Sync locked by another tab',
      };
    }

    try {
      console.log(`🔄 Starting bidirectional sync (pull-then-push) [device: ${this.deviceId.slice(0, 8)}]...`);

      // Step 1: Import (pull from cloud)
      let importResult: {
        success: boolean;
        hadManifest: boolean;
        skipped?: boolean;
        error?: string;
        schemaVersionError?: SchemaVersionError;
        manifest?: SyncManifest | null;
      };
      try {
        importResult = await this._doImport();
      } catch (importError) {
        // Import failed with exception - log but continue to export
        // Don't pass manifest since we don't know if it was read before failure
        const errorMsg = importError instanceof Error ? importError.message : 'Unknown import error';
        console.error('⚠️ Import failed (continuing to export):', errorMsg);
        importResult = { success: false, hadManifest: true, error: errorMsg };
      }

      // CRITICAL: Schema version errors block export - data format is incompatible
      // Other import failures (network, parse errors) can proceed to export since local data is valid
      if (importResult.schemaVersionError) {
        console.error(`🛑 Schema version error - blocking export [device: ${this.deviceId.slice(0, 8)}]`);
        // Return failed SyncResult with placeholder export fields (no export was performed)
        // Callers should check success=false first; schemaVersionError provides specific details
        return {
          success: false,
          type: 'baseline', // Placeholder - no export performed
          filename: '',     // Placeholder - no export performed
          dataExportedAt: '',   // Placeholder - no export performed
          dataImportedAt: '',   // Import failed due to schema error
          importOccurred: false,
          recordCount: 0,
          tableCount: 0,
          error: importResult.schemaVersionError.message,
          schemaVersionError: importResult.schemaVersionError,
        };
      }

      // Log import outcome with device context
      if (!importResult.success) {
        console.warn(`⚠️ Import step failed [device: ${this.deviceId.slice(0, 8)}]: ${importResult.error}`);
      } else if (importResult.skipped) {
        // Skipped is a benign outcome - this device was the last exporter, nothing new to pull
        console.log(`⏭️  Import skipped (no new remote data) [device: ${this.deviceId.slice(0, 8)}]`);
      } else if (!importResult.hadManifest) {
        console.log(`ℹ️  No cloud data to import (first sync or empty) [device: ${this.deviceId.slice(0, 8)}]`);
      }

      // Step 2: Export (push to cloud)
      // Proceed with export for non-schema import failures (local data still valid)
      // Pass cached manifest from import to avoid redundant download (only if import succeeded)
      const cachedManifest = importResult.success ? importResult.manifest : undefined;
      // Determine if import actually occurred (for timestamp tracking)
      const importOccurred = importResult.success && importResult.hadManifest && !importResult.skipped;
      const importTimestamp = importOccurred ? new Date().toISOString() : '';

      let exportResult: SyncResult;
      try {
        exportResult = await this._doExport(forceBaseline, cachedManifest);
      } catch (exportError) {
        console.error('❌ Export failed:', exportError);
        exportResult = {
          success: false,
          type: 'baseline',
          filename: '',
          dataExportedAt: '',
          dataImportedAt: importTimestamp,
          importOccurred,
          recordCount: 0,
          tableCount: 0,
          error: exportError instanceof Error ? exportError.message : 'Unknown export error',
        };
      }

      // Log overall outcome
      if (exportResult.success && !importResult.success && importResult.hadManifest) {
        // Partial success: export worked but import failed
        console.log(`⚠️ Bidirectional sync partial success: export OK, import failed [device: ${this.deviceId.slice(0, 8)}]`);
      } else if (exportResult.success) {
        console.log(`✅ Bidirectional sync complete [device: ${this.deviceId.slice(0, 8)}]`);
      } else {
        console.error(`❌ Bidirectional sync failed [device: ${this.deviceId.slice(0, 8)}]`);
      }

      // Return export result with import metadata added
      // Callers expect SyncResult aligned with export, but need import info for timestamp tracking
      return {
        ...exportResult,
        dataImportedAt: importTimestamp,
        importOccurred,
      };
    } finally {
      // Always release lock (cross-tab mutex)
      syncCoordinatorService.releaseExportLock();
    }
  }

  /**
   * Import data from sync provider (standalone, with lock management)
   *
   * Reads manifest, imports baseline, then applies all deltas in order.
   * For bidirectional sync, use bidirectionalSync() which handles both
   * import and export under a single lock.
   *
   * @returns True if import completed successfully (false if no manifest or failed)
   */
  async importFromProvider(): Promise<boolean> {
    if (!this.provider) {
      throw new Error('Sync provider not set');
    }

    // Check if provider is connected
    const isConnected = await this.provider.isConnected();
    if (!isConnected) {
      console.error('Sync provider not connected');
      return false;
    }

    // Acquire sync lock (cross-tab mutex with TTL)
    const lockAcquired = await syncCoordinatorService.tryAcquireExportLock();
    if (!lockAcquired) {
      console.log('Import skipped - another tab is syncing');
      return false;
    }

    try {
      console.log('🔄 Starting import (standalone)...');
      const result = await this._doImport();

      // Return true only if import actually happened and succeeded
      // (no manifest = false for standalone import, but bidirectionalSync handles differently)
      return result.success && result.hadManifest;
    } catch (error) {
      console.error('❌ Import failed:', error);
      return false;
    } finally {
      // Always release lock (cross-tab mutex)
      syncCoordinatorService.releaseExportLock();
    }
  }
}
